import React from "react";
import Select, { components } from 'react-select';
import CreatableSelect from 'react-select/creatable';

export const getReactSelectStyles = () => {
    const isDark = document.documentElement.classList.contains('dark');
    return {
        control: (provided, state) => ({
            ...provided,
            backgroundColor: isDark ? '#1e293b' : '#ffffff',
            borderColor: state.isFocused
                ? '#6366f1'
                : isDark ? '#334155' : '#cbd5e1',
            borderWidth: '1px',
            borderRadius: '0.5rem',
            minHeight: '38px',
            boxShadow: state.isFocused ? `0 0 0 2px ${isDark ? 'rgba(99, 102, 241, 0.35)' : 'rgba(99, 102, 241, 0.15)'}` : 'none',
            '&:hover': {
                borderColor: state.isFocused ? '#6366f1' : isDark ? '#475569' : '#94a3b8',
            },
        }),
        option: (provided, state) => ({
            ...provided,
            backgroundColor: state.isSelected
                ? '#4f46e5'
                : state.isFocused
                    ? isDark ? '#312e81' : '#f5f3ff'
                    : isDark ? '#1e293b' : '#ffffff',
            color: state.isSelected
                ? '#ffffff'
                : isDark ? '#f1f5f9' : '#374151',
            cursor: 'pointer',
            fontSize: '14px',
            '&:active': {
                backgroundColor: isDark ? '#3730a3' : '#eef2ff',
            },
        }),
        menu: (provided) => ({
            ...provided,
            backgroundColor: isDark ? '#1e293b' : '#ffffff',
            borderColor: isDark ? '#334155' : '#e2e8f0',
            borderWidth: '1px',
            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
        }),
        menuPortal: (provided) => ({
            ...provided,
            zIndex: 9999,
        }),
        singleValue: (provided) => ({
            ...provided,
            color: isDark ? '#f1f5f9' : '#374151',
            fontSize: '14px',
        }),
        placeholder: (provided) => ({
            ...provided,
            color: isDark ? '#64748b' : '#9ca3af',
            fontSize: '14px',
        }),
        multiValue: (provided) => ({
            ...provided,
            backgroundColor: isDark ? '#312e81' : '#eef2ff',
            borderRadius: '6px',
            padding: '1px 4px',
        }),
        multiValueLabel: (provided) => ({
            ...provided,
            color: isDark ? '#a5b4fc' : '#4f46e5',
            fontWeight: '500',
            fontSize: '12px',
        }),
        multiValueRemove: (provided) => ({
            ...provided,
            color: isDark ? '#818cf8' : '#818cf8',
            cursor: 'pointer',
            '&:hover': {
                backgroundColor: '#4f46e5',
                color: '#ffffff',
                borderRadius: '4px',
            },
        }),
    };
};

const formatOptionLabelDefault = (option) => (
    <div className="flex items-center gap-2">
        {option.__isNew__ ? (
            <span className="text-indigo-600 font-medium">+ Add &apos;{option.label}&apos; as new {option.createEntityName || 'option'}</span>
        ) : (
            <>
                {option.icon && (
                    <img
                        src={option.icon}
                        alt=""
                        className="w-5 h-3.5 object-cover rounded shadow-sm border border-slate-200 dark:border-slate-800"
                        onError={(e) => { e.target.style.display = 'none'; }}
                    />
                )}
                <span>{option.label}</span>
            </>
        )}
    </div>
);

const CreatableControl = ({ children, ...props }) => {
    const { inputValue, options, menuIsOpen } = props.selectProps;
    const trimmed = inputValue?.trim();
    const hasExactMatch = trimmed && options.some(
        (o) => !o.__isNew__ && o.label?.toLowerCase() === trimmed.toLowerCase()
    );
    const showNewBadge = menuIsOpen && trimmed && !hasExactMatch;

    return (
        <components.Control {...props}>
            {children}
            {showNewBadge && (
                <span className="react-select-new-badge" aria-hidden="true">NEW</span>
            )}
        </components.Control>
    );
};

const ReactSelect = ({
    options,
    selectedValue,
    handleSelectChange,
    Clearable,
    creatable = false,
    onCreateOption,
    createEntityName = 'option',
    isLoading = false,
}) => {
    const handleChange = (selectedOption) => {
        if (selectedOption?.__isNew__) return;
        handleSelectChange(selectedOption);
    };

    const sharedProps = {
        className: "basic-single",
        classNamePrefix: "select",
        isSearchable: true,
        name: "color",
        isClearable: Clearable === undefined ? true : Clearable,
        value: selectedValue,
        onChange: handleChange,
        options,
        menuPortalTarget: document.body,
        styles: getReactSelectStyles(),
        menuPosition: 'fixed',
        isLoading,
        formatOptionLabel: (option) => formatOptionLabelDefault({ ...option, createEntityName }),
    };

    if (creatable) {
        return (
            <CreatableSelect
                {...sharedProps}
                components={{ Control: CreatableControl }}
                formatCreateLabel={(inputValue) => inputValue}
                onCreateOption={onCreateOption}
                createOptionPosition="first"
                isValidNewOption={(inputValue, _value, opts) => {
                    const trimmed = inputValue.trim();
                    if (!trimmed) return false;
                    return !opts.some(
                        (o) => o.label?.toLowerCase() === trimmed.toLowerCase()
                    );
                }}
            />
        );
    }

    return (
        <Select
            {...sharedProps}
            formatOptionLabel={(option) => formatOptionLabelDefault(option)}
        />
    );
};

export default ReactSelect;
